/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/Shared/Icon";
import { Badge } from "../../components/ui/badge";
import { setBasicDialogOpenFor } from "../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../store/hooks";
import BasicDialog from "../../components/Shared/Dialogs/BasicDialog";
import CustomDataGrid from "../../components/Shared/Table/CustomDataGrid";
import { useTaskLogsDetailsData } from "./hooks";
import { prepareTaskLogDetailsCellDataGrid } from "../Configuration/helpers";

interface IAddEditAlarmForwardProps {
  taskName?: string;
  taskExecutionKey?: string;
  modelOpenMode?: string;
  initialStatus?: string;
}

interface StatusCardData {
  status: string;
  count: number;
}

const normalizeInitialStatus = (status?: string): string | null => {
  const value = String(status || "").trim();
  if (!value || value.toLowerCase() === "total") {
    return null;
  }

  const lower = value.toLowerCase();
  if (lower === "success" || lower === "successful" || lower === "finished") {
    return "Success";
  }
  if (lower === "failed" || lower === "fail") {
    return "Failed";
  }
  if (lower === "running") {
    return "Running";
  }
  if (lower === "idle") {
    return "Idle";
  }

  return value;
};

const TaskFailureDialog: React.FC<IAddEditAlarmForwardProps> = ({ taskName = "", taskExecutionKey = "", modelOpenMode = "Add", initialStatus }) => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, taskLogDetailsColumns } = useTaskLogsDetailsData(taskName, taskExecutionKey);

  const initialNormalizedStatus = useMemo(() => normalizeInitialStatus(initialStatus), [initialStatus]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(initialNormalizedStatus);
  const [isAggregatedMode, setIsAggregatedMode] = useState<boolean>(initialNormalizedStatus === "Success" || initialNormalizedStatus === "Failed");

  useEffect(() => {
    setSelectedStatus(initialNormalizedStatus);
    setIsAggregatedMode(initialNormalizedStatus === "Success" || initialNormalizedStatus === "Failed");
  }, [initialNormalizedStatus]);

  const rowsData = prepareTaskLogDetailsCellDataGrid(data, taskName) || [];

  const statusCards: StatusCardData[] = useMemo(() => {
    const statusMap = new Map<string, number>();

    rowsData.forEach((row: any) => {
      const status = row.status || "Unknown";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });

    const statusArray = Array.from(statusMap.entries())
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);

    return [{ status: "Total", count: rowsData.length }, ...statusArray];
  }, [rowsData]);

  const filteredRows = useMemo(() => {
    if (!selectedStatus || selectedStatus === "Total") {
      return rowsData;
    }

    if (isAggregatedMode && selectedStatus === "Success") {
      return rowsData.filter((row: any) => {
        const status = String(row.status || "").toLowerCase();
        return status === "successful" || status === "finished";
      });
    }

    if (isAggregatedMode && selectedStatus === "Failed") {
      return rowsData.filter((row: any) => {
        const status = String(row.status || "").toLowerCase();
        return status === "failed" || status === "timeout" || status === "aborted" || status === "stopped";
      });
    }

    return rowsData.filter((row: any) => String(row.status || "").toLowerCase() === selectedStatus.toLowerCase());
  }, [rowsData, selectedStatus, isAggregatedMode]);

  const handleStatusClick = (status: string) => {
    if (status === "Total") {
      setSelectedStatus(null);
      setIsAggregatedMode(false);
      return;
    }

    setSelectedStatus(selectedStatus === status ? null : status);
    setIsAggregatedMode(false);
  };

  const getStatusColor = (status: string): string => {
    if (status === "Total") return "var(--primary)";

    const statusLower = status.toLowerCase();

    if (statusLower === "successful" || statusLower === "finished") return "var(--success)";
    if (statusLower === "failed" || statusLower === "aborted" || statusLower === "timeout") return "var(--destructive)";
    if (statusLower === "running") return "var(--link)";
    if (statusLower === "stopped" || statusLower === "suspended") return "var(--warning)";
    if (statusLower === "idle") return "var(--muted-foreground)";
    if (statusLower === "missed execution (server down)" || statusLower.includes("missed execution")) return "var(--destructive)";
    if (statusLower.includes("success") || statusLower.includes("complete")) return "var(--success)";
    if (statusLower.includes("fail") || statusLower.includes("error")) return "var(--destructive)";
    if (statusLower.includes("warning") || statusLower.includes("pending")) return "var(--warning)";
    if (statusLower.includes("running") || statusLower.includes("progress")) return "var(--link)";

    return "var(--muted-foreground)";
  };

  const exportToCSV = () => {
    try {
      if (filteredRows.length === 0) {
        return;
      }

      const headers = ["Name", "Status", "Progress", "Details"];
      const csvContent = [
        headers.join(","),
        ...filteredRows.map((row: any) => {
          const name = `"${(row.name || "").replace(/"/g, '""')}"`;
          const status = `"${(row.status || "").replace(/"/g, '""')}"`;
          const percentage = row.percentage || 0;
          const details = `"${(row.details || "").replace(/"/g, '""')}"`;
          return `${name},${status},${percentage},${details}`;
        })
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
      const statusPart = selectedStatus ? `_${selectedStatus.replace(/\s+/g, "_")}` : "_All";
      const fileName = `task_run_details_${taskName}${statusPart}_${timestamp}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
    }
  };

  const dialogAction = (
    <div className="flex justify-end items-center py-2">
      <Button onClick={() => dispatch(setBasicDialogOpenFor(undefined))}>Cancel</Button>
    </div>
  );

  const isCardHighlighted = (cardStatus: string): boolean => {
    if (!selectedStatus) return cardStatus === "Total";
    if (selectedStatus === cardStatus) return true;

    if (isAggregatedMode) {
      const statusLower = cardStatus.toLowerCase();
      if (selectedStatus === "Success") {
        return statusLower === "successful" || statusLower === "finished";
      }
      if (selectedStatus === "Failed") {
        return statusLower === "failed" || statusLower === "timeout" || statusLower === "aborted" || statusLower === "stopped";
      }
    }

    return false;
  };

  return (
    <BasicDialog size="md" headerTitle={`Task Run Details`} dialogActions={dialogAction}>
      <div className="relative p-2" style={{ color: "var(--foreground)" }}>
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            {statusCards.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {statusCards.map((statusCard) => {
                  const isHighlighted = isCardHighlighted(statusCard.status);
                  return (
                    <div
                      key={statusCard.status}
                      className="cursor-pointer select-none rounded-md transition-all hover:shadow-md hover:-translate-y-0.5"
                      style={{
                        border: isHighlighted ? "2px solid" : "1px solid",
                        borderColor: isHighlighted ? getStatusColor(statusCard.status) : "var(--border)",
                        backgroundColor: isHighlighted ? `${getStatusColor(statusCard.status)}15` : "var(--card)",
                        minWidth: "120px",
                      }}
                      onClick={() => handleStatusClick(statusCard.status)}
                    >
                      <div className="flex flex-col items-start justify-center p-1.5">
                        <span
                          className="text-foreground mb-0.5"
                          style={{
                            fontWeight: isHighlighted ? 600 : 500,
                            fontSize: "0.8rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {statusCard.status}
                        </span>
                        <Badge
                          className="text-xs font-semibold px-2 py-0.5"
                          style={{
                            backgroundColor: getStatusColor(statusCard.status),
                            color: "var(--primary-foreground)",
                            minWidth: "28px",
                          }}
                        >
                          {statusCard.count}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="shrink-0 ml-2 mt-10">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={exportToCSV}
              disabled={filteredRows.length === 0}
              aria-label="Export CSV"
            >
              <Icon name="download" size={18} />
            </Button>
          </div>
        </div>

        <div className="mt-1">
          <CustomDataGrid
            tableId="taskFailureDialog"
            sx={{
              height: "410px",
              "& .MuiDataGrid-virtualScroller": { overflowY: "auto" },
              "& .MuiDataGrid-scrollbar--vertical": { display: "block" },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
            rows={filteredRows}
            columns={taskLogDetailsColumns}
            loading={isLoading}
            hideFooter={true}
          />
        </div>
      </div>
    </BasicDialog>
  );
};

export default TaskFailureDialog;
